import { cn } from "@newxsystems/shared";
import type { CSSProperties } from "react";
import type {
  ImageListingBlockBadgeProps,
  ImageListingBlockItemProps,
  ImageListingBlockLinkProps,
  IImageListingBlockProps,
} from "./ImageListingBlock.props";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import { KaajAction } from "../_shared/KaajAction";
import { RichTextHtml } from "../../RichTextHtml";

const getRel = (href: string | undefined, target: string | undefined) => {
  if (target === "_blank" || href?.startsWith("http")) {
    return "noopener noreferrer";
  }

  return undefined;
};

const getImageAlt = (item: ImageListingBlockItemProps) => item.image.alternateText;

const getObjectPosition = (item: ImageListingBlockItemProps): CSSProperties | undefined => {
  const focalPoint = item.image.focalPoint;

  return focalPoint ? { objectPosition: `${focalPoint.x}% ${focalPoint.y}%` } : undefined;
};

const getItemLink = (item: ImageListingBlockItemProps): ImageListingBlockLinkProps | undefined =>
  item.link ?? undefined;

const badgeToneClassName: Record<NonNullable<ImageListingBlockBadgeProps["tone"]>, string> = {
  accent: "border-[#CAD7FF] bg-[#EEF2FF] text-[#2144FF]",
  neutral: "border-[#D9E1FF] bg-white text-[#405076]",
  success: "border-[#A7EFE3] bg-[#E8FBF7] text-[#167568]",
  warning: "border-[#FFE59C] bg-[#FFF7D8] text-[#765000]",
};

const Badge = ({ badge }: { badge?: ImageListingBlockBadgeProps | null }) => {
  if (!badge?.text) {
    return null;
  }

  return (
    <span
      className={cn(
        "inline-flex w-fit rounded-full border px-3 py-1 text-[0.68rem] font-extrabold uppercase tracking-[0.16em] shadow-[0_8px_20px_rgba(33,68,255,0.08)]",
        badgeToneClassName[badge.tone ?? "neutral"],
      )}
    >
      {badge.text}
    </span>
  );
};

const Badges = ({ badges }: { badges?: ImageListingBlockItemProps["badges"] }) => {
  if (!badges?.length) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {badges.map((badge, index) => (
        <Badge badge={badge} key={`${badge.text}-${index}`} />
      ))}
    </div>
  );
};

const ImageFrame = ({
  index,
  item,
  variant,
}: {
  index: number;
  item: ImageListingBlockItemProps;
  variant: IImageListingBlockProps["variant"];
}) => {
  const Image = resolveKaajPrimitive("Image");
  const isHorizontal = variant === "horizontal-list";
  const isThreeColumn = variant === "three-column";
  const eagerCount = isThreeColumn ? 3 : isHorizontal ? 3 : 4;

  return (
    <figure
      className={cn(
        "relative overflow-hidden border border-[#D9E1FF] bg-white",
        isHorizontal
          ? "w-full rounded-[1.25rem] shadow-[0_10px_28px_rgba(33,68,255,0.07)] sm:w-44 sm:shrink-0 lg:w-52 xl:w-56"
          : "w-full p-0 shadow-[0_16px_46px_rgba(33,68,255,0.08)]",
        !isHorizontal && (isThreeColumn ? "rounded-[1.35rem]" : "rounded-[1.45rem]"),
      )}
    >
      <div
        className={cn(
          "relative overflow-hidden bg-[#FAFBFF]",
          isThreeColumn ? "rounded-[0.95rem]" : "rounded-[1.1rem]",
        )}
      >
        <Image
          alt={getImageAlt(item)}
          className={cn(
            "h-full w-full object-cover transition duration-500 motion-reduce:transition-none motion-safe:group-hover:scale-[1.025]",
            isHorizontal
              ? "aspect-[16/10] sm:aspect-[4/3]"
              : isThreeColumn
                ? "aspect-[16/11]"
                : "aspect-[4/3]",
          )}
          decoding="async"
          height={item.image.height ?? undefined}
          loading={index < eagerCount ? "eager" : "lazy"}
          priority={index === 0}
          sizes={
            item.image.sizes ??
            (isHorizontal
              ? "(min-width: 1280px) 15rem, (min-width: 1024px) 14rem, (min-width: 640px) 12rem, 92vw"
              : isThreeColumn
                ? "(min-width: 1280px) 22rem, (min-width: 768px) 45vw, 92vw"
                : "(min-width: 1024px) 34rem, 92vw")
          }
          src={item.image.defaultSrc}
          srcSet={item.image.srcSet ?? undefined}
          style={getObjectPosition(item)}
          width={item.image.width ?? undefined}
        />
        {isThreeColumn && item.badges?.length ? (
          <div className="absolute left-3 top-3">
            <Badges badges={item.badges} />
          </div>
        ) : null}
      </div>
    </figure>
  );
};

const Tags = ({
  compact = false,
  tags,
}: {
  compact?: boolean;
  tags?: ImageListingBlockItemProps["tags"];
}) => {
  const visibleTags = tags ?? [];

  if (!visibleTags.length) {
    return null;
  }

  return (
    <ul className={cn("flex list-none flex-wrap p-0", compact ? "mt-4 gap-1.5" : "mt-5 gap-2")}>
      {visibleTags.map((tag, index) => (
        <li
          key={`${tag.tag}-${index}`}
          className={cn(
            "rounded-full border border-[#CAD7FF] bg-white/90 font-semibold text-[#405076] shadow-[0_6px_16px_rgba(33,68,255,0.06)]",
            compact ? "px-2.5 py-1 text-[0.72rem]" : "px-3 py-1 text-xs",
          )}
        >
          {tag.tag}
        </li>
      ))}
    </ul>
  );
};

const CardCopy = ({
  compact = false,
  item,
  showBadge = true,
}: {
  compact?: boolean;
  item: ImageListingBlockItemProps;
  showBadge?: boolean;
}) => (
  <div className="flex min-w-0 flex-1 flex-col">
    {showBadge ? <Badges badges={item.badges} /> : null}
    <h3
      className={cn(
        "text-balance font-extrabold leading-tight tracking-[-0.02em] text-[#151927]",
        compact ? "text-[1.18rem] lg:text-[1.28rem]" : "text-xl lg:text-2xl",
        showBadge && item.badges?.length && "mt-4",
      )}
    >
      {item.heading}
    </h3>
    <RichTextHtml
      className={cn(
        "mt-3 text-pretty leading-6 text-[#4B5568] [overflow-wrap:anywhere]",
        compact ? "text-[0.95rem]" : "text-sm lg:text-base lg:leading-7",
      )}
      html={item.subheading}
    />
    <Tags compact={compact} tags={item.tags} />
  </div>
);

const ItemLink = ({
  appearance = "text",
  item,
}: {
  appearance?: "text" | "pill";
  item: ImageListingBlockItemProps;
}) => {
  const link = getItemLink(item);

  if (!link?.href) {
    return null;
  }

  const Link = resolveKaajPrimitive("Link");

  return (
    <Link
      href={link.href ?? ""}
      target={link.target ?? undefined}
      rel={getRel(link.href ?? undefined, link.target ?? undefined)}
      aria-label={link.ariaLabel ?? undefined}
      className={cn(
        "inline-flex min-h-11 w-fit items-center rounded-full text-sm font-extrabold transition-[background-color,border-color,box-shadow,color,decoration-color,transform] duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2144FF] motion-reduce:transition-none motion-safe:hover:translate-x-0.5",
        appearance === "pill"
          ? "justify-center border border-[#CAD7FF] bg-[#EEF2FF] px-4 py-2 text-[#2144FF] shadow-[0_12px_28px_rgba(33,68,255,0.12)] hover:border-[#2144FF] hover:bg-white hover:text-[#132EDB] hover:shadow-[0_16px_34px_rgba(33,68,255,0.16)]"
          : "mt-auto text-[#2144FF] underline decoration-[#2144FF]/20 underline-offset-4 hover:text-[#132EDB] hover:decoration-[#132EDB]",
      )}
    >
      {link.text}
      <span aria-hidden="true" className="ml-2">
        →
      </span>
    </Link>
  );
};

const GridCard = ({
  index,
  item,
  variant,
}: {
  index: number;
  item: ImageListingBlockItemProps;
  variant: IImageListingBlockProps["variant"];
}) => {
  const isThreeColumn = variant === "three-column";

  return (
    <article
      className={cn(
        "group relative isolate flex h-full min-w-0 flex-col overflow-hidden border border-[#D9E1FF] bg-white/95 p-2 shadow-[0_20px_58px_rgba(33,68,255,0.09)] transition-[border-color,box-shadow,transform] duration-300 hover:border-[#BFD0FF] hover:bg-white hover:shadow-[0_28px_74px_rgba(33,68,255,0.14)] motion-reduce:transition-none motion-safe:hover:-translate-y-0.5",
        isThreeColumn ? "rounded-[1.65rem] sm:p-2.5" : "rounded-[2rem] sm:p-3",
      )}
    >
      <ImageFrame index={index} item={item} variant={variant} />
      <div
        className={cn(
          "flex flex-1 flex-col",
          isThreeColumn ? "px-4 pb-5 pt-5 sm:px-5" : "px-4 pb-4 pt-6 sm:px-5 sm:pb-5 sm:pt-7",
        )}
      >
        <CardCopy compact={isThreeColumn} item={item} showBadge={!isThreeColumn} />
        <div className={cn(isThreeColumn ? "mt-auto pt-5" : "pt-6")}>
          <ItemLink item={item} />
        </div>
      </div>
    </article>
  );
};

const HorizontalCard = ({ index, item }: { index: number; item: ImageListingBlockItemProps }) => (
  <article className="group relative isolate grid min-w-0 gap-5 px-4 py-5 transition-colors duration-200 hover:bg-[#FAFBFF]/90 motion-reduce:transition-none sm:grid-cols-[11rem_minmax(0,1fr)] sm:items-center sm:px-5 lg:grid-cols-[13rem_minmax(0,1fr)_auto] lg:gap-7 lg:px-6 lg:py-6 xl:grid-cols-[14rem_minmax(0,1fr)_auto]">
    <ImageFrame index={index} item={item} variant="horizontal-list" />
    <div className="flex min-w-0 flex-1 flex-col sm:pr-4 lg:py-1">
      <CardCopy item={item} />
    </div>
    <div className="flex sm:col-start-2 lg:col-start-auto lg:items-center lg:justify-end lg:py-1">
      <ItemLink appearance="pill" item={item} />
    </div>
  </article>
);

export const ImageListingBlock = ({
  anchorId,
  buttons,
  parentClass,
  spacing,
  variant,
  items,
  eyebrow,
  heading,
  subheading,
}: IImageListingBlockProps) => {
  const hasHeader = Boolean(eyebrow || heading || subheading);
  const headingId = heading && anchorId ? `${anchorId}-heading` : undefined;

  if (!hasHeader && !items?.length && !buttons?.length) {
    return null;
  }

  return (
    <section
      className={cn(
        "relative isolate overflow-hidden text-[#151927]",
        getKaajSectionSpacing(spacing),
        parentClass,
      )}
      aria-labelledby={headingId}
      id={anchorId ?? undefined}
    >
      <div className="kaaj-container relative">
        {hasHeader ? (
          <div className="mb-10 grid gap-6 lg:mb-14">
            <div className="">
              {eyebrow ? (
                <p className="mb-4 text-xs font-extrabold uppercase tracking-[0.18em] text-[#2144FF] [overflow-wrap:anywhere]">
                  {eyebrow}
                </p>
              ) : null}
              {heading ? (
                <h2 id={headingId} className="kaaj-section-heading text-[#151927]">
                  {heading}
                </h2>
              ) : null}
            </div>
            {subheading ? (
              <RichTextHtml
                className="text-pretty text-base leading-8 text-[#405076] [overflow-wrap:anywhere]"
                html={subheading}
              />
            ) : null}
          </div>
        ) : null}

        {items?.length && variant === "horizontal-list" ? (
          <div className="overflow-hidden rounded-[2.1rem] border border-[#D9E1FF] bg-white/90 shadow-[0_24px_72px_rgba(33,68,255,0.1)] divide-y divide-[#D9E1FF]">
            {items.map((item, index) => (
              <HorizontalCard
                index={index}
                item={item}
                key={`${item.heading}-${item.image.defaultSrc}`}
              />
            ))}
          </div>
        ) : items?.length ? (
          <div
            className={cn(
              "grid gap-5 lg:gap-6",
              variant === "three-column" ? "md:grid-cols-2 xl:grid-cols-3" : "lg:grid-cols-2",
            )}
          >
            {items.map((item, index) => (
              <GridCard
                index={index}
                item={item}
                key={`${item.heading}-${item.image.defaultSrc}`}
                variant={variant}
              />
            ))}
          </div>
        ) : null}

        {buttons?.length ? (
          <div className="mt-8 flex flex-wrap gap-3">
            {buttons.map((button, index) => (
              <KaajAction
                ariaLabel={button.ariaLabel}
                href={button.href}
                key={`${button.href}-${button.text}-${index}`}
                label={button.text}
                target={button.target}
                variant={button.style}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

ImageListingBlock.displayName = "Block::ImageListingBlock";
